import { MoreVert } from '@mui/icons-material';
import { Box, Button, IconButton, Link, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { Cell } from './data-grid';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';
import { TablaCard, TablaCardRow } from './tabla-card';

/** Date / Quantity / Estimated yield / per-sample link. */
const COLUMNS = 'repeat(4, minmax(0, 1fr))';

const RegistryYieldEstimation = ({
    cropMonitoring,
    notes,
    onViewResults,
    onAddEstimation,
    expanded,
    onToggle,
}: {
    cropMonitoring: CropMonitoring;
    notes: CropMonitoringNote[];
    /** Opens a specific estimation's read-only per-sample results ("Resultado por muestras"). */
    onViewResults?: (estimationId: number) => void;
    /** Opens a blank form to add another estimation to the campaign. */
    onAddEstimation?: () => void;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    const units = useUnitProvider();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    // API returns estimations newest-first.
    const estimations = cropMonitoring.cornCropMonitoring?.yieldEstimations ?? [];
    const newest = estimations[0];

    const userNodeQuery = useGetUserNode(newest?.createdBy ?? 0);

    const closeMenu = () => setMenuAnchor(null);
    const handleAdd = () => {
        closeMenu();
        onAddEstimation?.();
    };

    if (estimations.length === 0) return null;

    const authorName = getUserDisplayName(userNodeQuery.data);
    const registeredAt = newest?.created ? formatDate(new Date(newest.created)) : null;

    return (
        <RegistryCard
            header={
                <Typography variant='h6' color='primary'>
                    {_('Estimación de rinde')}
                </Typography>
            }
            menu={
                // Mobile shows a visible "Agregar estimación" button in the body instead (see
                // below) — no "more options" dropdown there, matching the Figma mock.
                onToggle ? undefined : (
                    <>
                        <IconButton
                            size='small'
                            aria-label={_('Más opciones')}
                            onClick={(event) => setMenuAnchor(event.currentTarget)}
                        >
                            <MoreVert />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                            <MenuItem onClick={handleAdd}>{_('Agregar estimación')}</MenuItem>
                        </Menu>
                    </>
                )
            }
            footer={registeredAt && <RegistryFooter authorName={authorName} date={registeredAt} />}
            expanded={expanded}
            onToggle={onToggle}
        >
            {onToggle ? (
                <Stack spacing={1} sx={{ width: '100%' }}>
                    {estimations.map((estimation) => (
                        <TablaCard key={estimation.id}>
                            <TablaCardRow
                                label={_('Fecha del muestreo')}
                                value={estimation.created ? formatDate(new Date(estimation.created)) : '—'}
                            />
                            <TablaCardRow
                                label={_('Cantidad')}
                                value={`${estimation.yieldSamples?.length ?? 0} ${_('muestras')}`}
                            />
                            <TablaCardRow
                                label={_('Rendimiento estimado')}
                                value={
                                    estimation.result != null
                                        ? units.toYield(estimation.result, cropMonitoring.crop, 2).toString()
                                        : '—'
                                }
                            />
                            <Link
                                component='button'
                                variant='body2'
                                color='secondary'
                                underline='always'
                                sx={{ alignSelf: 'flex-start' }}
                                onClick={() => onViewResults?.(estimation.id)}
                            >
                                {_('Resultado por muestras')}
                            </Link>
                        </TablaCard>
                    ))}
                </Stack>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, width: '100%', overflow: 'auto' }}>
                    <Cell header>{_('Fecha del muestreo')}</Cell>
                    <Cell header>{_('Cantidad')}</Cell>
                    <Cell header>{_('Rendimiento estimado')}</Cell>
                    <Cell header>{''}</Cell>
                    {estimations.map((estimation) => (
                        <Fragment key={estimation.id}>
                            <Cell>{estimation.created ? formatDate(new Date(estimation.created)) : '—'}</Cell>
                            <Cell>{`${estimation.yieldSamples?.length ?? 0} ${_('muestras')}`}</Cell>
                            <Cell>
                                {estimation.result != null
                                    ? units.toYield(estimation.result, cropMonitoring.crop, 2).toString()
                                    : '—'}
                            </Cell>
                            <Cell>
                                <Link
                                    component='button'
                                    color='secondary'
                                    underline='always'
                                    onClick={() => onViewResults?.(estimation.id)}
                                >
                                    {_('Resultado por muestras')}
                                </Link>
                            </Cell>
                        </Fragment>
                    ))}
                </Box>
            )}

            {newest && (
                <RegistryNotes
                    notes={notes}
                    fieldId={cropMonitoring.field}
                    cornYieldEstimationId={newest.id}
                    cropMonitoring={cropMonitoring}
                    registryType='yield_estimation'
                    registryOrigin='manual'
                />
            )}

            {onToggle && onAddEstimation && (
                <Button variant='outlined' color='primary' fullWidth onClick={onAddEstimation}>
                    {_('Agregar estimación')}
                </Button>
            )}
        </RegistryCard>
    );
};

export default RegistryYieldEstimation;
